import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useOutletContext } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import { withStyles } from '@mui/styles'
import { Box } from '@mui/material'
import DialogSubsectionHeadline from '../../../../Header/DialogSubsectionHeadline'
import Footer from './Footer'
import ImageUpload from '../../../../@Common/Pictures/ImageUpload'

const bulletStyle = {
    fontFamily: 'Roboto Regular, Helvetica, sans-serif',
    fontSize: '14px',
    lineHeight: '16px',
    color: '#2A2D36',
    margin: 0,
    paddingLeft: '10px',
    textIndent: '-10px',
}

function BulletDescription({ children }) {
    return <p style={bulletStyle}>{children}</p>
}
import {
    useGetSellerContentQuery,
    useUpdateSellerContentMutation,
} from '../../../../../redux/slices/sellerContentSlice'
import { showSuccesNotification, showErrorNotification, saveAffiliatePicture } from '../../../../../redux/actions'
import { deleteAffiliatePicture } from '../../../../../redux/modules/affiliates/affiliates'
import { useForm } from 'react-hook-form'
import { MUIFormTextAreaInput } from '../../../../@Common/Form/TextAreaInput/FormTextAreaInput'
import { MUIFormTextInput } from '../../../../@Common/Form/TextInput/FormTextInput'
import { MUISwitchInput } from '../../../../@Common/Form/SwitchInput/ControlledSwitchInput'
import {
    previewImageDimensionsValid,
    imageHeightValid,
} from '../../../../../validation'

const styles = {
    inputDescription: {
        fontFamily: 'Roboto Regular, Helvetica, sans-serif',
        fontSize: '14px',
        lineHeight: '16px',
        color: '#2A2D36',
        margin: 0,
    },
    errorText: {
        color: '#d32f2f',
        fontSize: '12px',
        fontFamily: 'Roboto Regular, Helvetica, sans-serif',
        marginTop: '4px',
    },
}

function SellerCustomizePageSection({ classes }) {
    const dispatch = useDispatch()
    const { canUpdate } = useOutletContext()
    const { id: partnerListingId } = useParams()
    const { data: sellerContent, isLoading, refetch } = useGetSellerContentQuery(partnerListingId)
    const [updateSellerContent] = useUpdateSellerContentMutation()
    const [processing, setProcessing] = useState(false)

    const [logoState, setLogoState] = useState(null)
    const [desktopHeroState, setDesktopHeroState] = useState(null)
    const [mobileHeroState, setMobileHeroState] = useState(null)
    const [initialized, setInitialized] = useState(false)
    const [logoError, setLogoError] = useState(null)
    const [desktopHeroError, setDesktopHeroError] = useState(null)
    const [mobileHeroError, setMobileHeroError] = useState(null)
    const [displaySellerMessage, setDisplaySellerMessage] = useState(false)

    const { control, handleSubmit, reset } = useForm({
        defaultValues: {
            description: '',
            howItWorks: '',
            tagline: '',
            sellerMessage: '',
            seoSlugLiteral: '',
        },
    })

    // Initialize form when data loads
    React.useEffect(() => {
        if (sellerContent && !initialized) {
            reset({
                description: sellerContent.description || '',
                howItWorks: sellerContent.howItWorks || '',
                tagline: sellerContent.tagline || '',
                sellerMessage: sellerContent.sellerMessage || '',
                seoSlugLiteral: sellerContent.seoSlugLiteral || '',
            })
            setDisplaySellerMessage(sellerContent.displaySellerMessage || false)
            setLogoState({
                url: sellerContent.logo?.pictureId ? sellerContent.logo.imageUrl : null,
                id: sellerContent.logo?.pictureId || 0,
            })
            setDesktopHeroState({
                url: sellerContent.desktopHero?.pictureId ? sellerContent.desktopHero.imageUrl : null,
                id: sellerContent.desktopHero?.pictureId || 0,
            })
            setMobileHeroState({
                url: sellerContent.mobileHero?.pictureId ? sellerContent.mobileHero.imageUrl : null,
                id: sellerContent.mobileHero?.pictureId || 0,
            })
            setInitialized(true)
        }
    }, [sellerContent, initialized, reset])

    const onSubmit = async (values) => {
        // Validate image dimensions on submit (matching Marketplace pattern)
        const errors = {}
        if (logoState?.url && typeof logoState.url !== 'string') {
            const logoHeightError = imageHeightValid(40, null, 'sellerLogoImagePreview')
            if (logoHeightError) errors.logo = logoHeightError
        }
        if (desktopHeroState?.url && typeof desktopHeroState.url !== 'string') {
            const desktopError = previewImageDimensionsValid(1800, 500, 'sellerDesktopHeroImagePreview')
            if (desktopError) errors.desktopHero = desktopError
        }
        if (mobileHeroState?.url && typeof mobileHeroState.url !== 'string') {
            const mobileError = previewImageDimensionsValid(800, 500, 'sellerMobileHeroImagePreview')
            if (mobileError) errors.mobileHero = mobileError
        }
        if (Object.keys(errors).length > 0) {
            if (errors.logo) setLogoError(errors.logo)
            if (errors.desktopHero) setDesktopHeroError(errors.desktopHero)
            if (errors.mobileHero) setMobileHeroError(errors.mobileHero)
            return
        }

        setProcessing(true)
        try {
            let freshContent
            try {
                freshContent = await refetch().unwrap()
            } catch (err) {
                // A 404 just means there's no seller content yet; the no-seller guard below handles it.
                if (err?.status !== 404) throw err
            }

            // No seller exists for this listing (e.g. deep-linked to the seller tab) — the update has
            // nothing to target, so stop instead of sending a payload with an undefined sellerId.
            const sellerId = freshContent?.sellerId ?? sellerContent?.sellerId
            if (!sellerId) {
                dispatch(showErrorNotification('This listing has no seller yet, so there is nothing to save.'))
                return
            }

            const promises = []

            // Handle image uploads
            if (logoState?.url && typeof logoState.url !== 'string' && logoState.id === 0) {
                const formData = new window.FormData()
                formData.append('file', logoState.url)
                formData.append('seoFilename', 'sellerLogoImage')
                formData.append('targetSize', 200)
                promises.push(dispatch(saveAffiliatePicture(partnerListingId, 'logo', formData)))
            }
            if (desktopHeroState?.url && typeof desktopHeroState.url !== 'string' && desktopHeroState.id === 0) {
                const formData = new window.FormData()
                formData.append('file', desktopHeroState.url)
                formData.append('seoFilename', 'sellerHeroImageDesktop')
                formData.append('targetSize', 200)
                promises.push(dispatch(saveAffiliatePicture(partnerListingId, 'desktop', formData)))
            }
            if (mobileHeroState?.url && typeof mobileHeroState.url !== 'string' && mobileHeroState.id === 0) {
                const formData = new window.FormData()
                formData.append('file', mobileHeroState.url)
                formData.append('seoFilename', 'sellerHeroImageMobile')
                formData.append('targetSize', 200)
                promises.push(dispatch(saveAffiliatePicture(partnerListingId, 'mobile', formData)))
            }

            let logoResult = { id: logoState?.id || 0, url: logoState?.url }
            let desktopResult = { id: desktopHeroState?.id || 0, url: desktopHeroState?.url }
            let mobileResult = { id: mobileHeroState?.id || 0, url: mobileHeroState?.url }

            if (promises.length > 0) {
                const responses = await Promise.all(promises)
                responses.forEach((r) => {
                    if (r.pictureType === 'logo') {
                        logoResult = { id: r.id, url: r.url }
                    } else if (r.pictureType === 'desktop') {
                        desktopResult = { id: r.id, url: r.url }
                    } else if (r.pictureType === 'mobile') {
                        mobileResult = { id: r.id, url: r.url }
                    }
                })
            }

            // Handle deleted images
            if (logoState?.url === null && sellerContent?.logo?.pictureId > 0) {
                await dispatch(deleteAffiliatePicture(sellerContent.logo.pictureId))
                logoResult = { id: 0, url: null }
            }
            if (desktopHeroState?.url === null && sellerContent?.desktopHero?.pictureId > 0) {
                await dispatch(deleteAffiliatePicture(sellerContent.desktopHero.pictureId))
                desktopResult = { id: 0, url: null }
            }
            if (mobileHeroState?.url === null && sellerContent?.mobileHero?.pictureId > 0) {
                await dispatch(deleteAffiliatePicture(sellerContent.mobileHero.pictureId))
                mobileResult = { id: 0, url: null }
            }

            const payload = {
                sellerId,
                sellerName: freshContent?.sellerName || '',
                sellerWebsite: freshContent?.sellerWebsite || '',
                status: freshContent?.internalStatus || sellerContent?.internalStatus || freshContent?.status || sellerContent?.status || 'Off',
                description: values.description,
                howItWorks: values.howItWorks,
                tagline: values.tagline,
                sellerMessage: values.sellerMessage,
                displaySellerMessage: displaySellerMessage,
                seoSlugLiteral: values.seoSlugLiteral,
                logo: { pictureId: logoResult.id, altTag: freshContent?.sellerName || sellerContent?.sellerName || '' },
                desktopHero: { pictureId: desktopResult.id, altTag: freshContent?.sellerName || sellerContent?.sellerName || '' },
                mobileHero: { pictureId: mobileResult.id, altTag: freshContent?.sellerName || sellerContent?.sellerName || '' },
                // Preserve marketing assets fields (prefer the fresh read so a concurrent marketing save isn't clobbered)
                promoSquare: freshContent?.promoSquare ?? sellerContent?.promoSquare ?? { pictureId: 0, altTag: '' },
                promoTile: freshContent?.promoTile ?? sellerContent?.promoTile ?? { pictureId: 0, altTag: '' },
                location: freshContent?.location ?? null,
            }

            await updateSellerContent(payload).unwrap()

            // Sync image state to the just-persisted ids/urls so a subsequent same-session save does
            // not treat already-uploaded images as new uploads (BTP-24621). The *Result values hold
            // the persisted picture id and url (string) after upload, or the existing values.
            setLogoState({ url: typeof logoResult.url === 'string' ? logoResult.url : null, id: logoResult.id })
            setDesktopHeroState({ url: typeof desktopResult.url === 'string' ? desktopResult.url : null, id: desktopResult.id })
            setMobileHeroState({ url: typeof mobileResult.url === 'string' ? mobileResult.url : null, id: mobileResult.id })

            dispatch(showSuccesNotification('Seller page settings saved successfully.'))
        } catch (err) {
            dispatch(showErrorNotification('There was an error saving seller page settings.'))
        } finally {
            setProcessing(false)
        }
    }

    if (isLoading) {
        return (
            <div style={{ position: 'relative', minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </div>
        )
    }

    return (
        <div style={{ position: 'relative', minHeight: '800px' }}>
            {processing && (
                <div style={{
                    position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                    minHeight: '400px', backgroundColor: 'rgba(255,255,255,.8)',
                    overflow: 'hidden', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center',
                }}>
                    <CircularProgress />
                </div>
            )}
            <div style={{ fontSize: '14px', minHeight: '55vh' }}>
                <Grid container columnSpacing={5} style={{ marginTop: '24px' }}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">Logo</DialogSubsectionHeadline>
                        <BulletDescription>• Minimum Height: 40px for horizontal logos / 100px for square or vertical logos.</BulletDescription>
                        <BulletDescription>• Supports JPG, JPEG, PNG</BulletDescription>
                        <BulletDescription>• Max file size 2MB</BulletDescription>
                    </Grid>
                    <Grid item xs={7}>
                        <ImageUpload
                            variant="logo"
                            imageUrl={logoState?.url}
                            imageId={logoState?.id || 0}
                            previewId="sellerLogoImagePreview"
                            maxSizeMB={2}
                            error={logoError}
                            validate={setLogoError}
                            onEdit={(file) => setLogoState({ url: file, id: 0 })}
                            onDelete={() => { setLogoState({ url: null, id: 0 }); setLogoError(null) }}
                        />
                    </Grid>
                </Grid>

                <Grid container columnSpacing={5} style={{ marginTop: '24px' }}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">Desktop Hero Image</DialogSubsectionHeadline>
                        <BulletDescription>• Dimensions: 1800 x 500px</BulletDescription>
                        <BulletDescription>• Supports JPG, JPEG, PNG</BulletDescription>
                        <BulletDescription>• Max file size 2MB</BulletDescription>
                    </Grid>
                    <Grid item xs={7}>
                        <ImageUpload
                            variant="desktopHero"
                            imageUrl={desktopHeroState?.url}
                            imageId={desktopHeroState?.id || 0}
                            previewId="sellerDesktopHeroImagePreview"
                            maxSizeMB={2}
                            error={desktopHeroError}
                            validate={setDesktopHeroError}
                            onEdit={(file) => setDesktopHeroState({ url: file, id: 0 })}
                            onDelete={() => { setDesktopHeroState({ url: null, id: 0 }); setDesktopHeroError(null) }}
                        />
                    </Grid>
                </Grid>

                <Grid container columnSpacing={5} style={{ marginTop: '24px' }}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">Mobile Hero Image</DialogSubsectionHeadline>
                        <BulletDescription>• Dimensions: 800 x 500px</BulletDescription>
                        <BulletDescription>• Supports JPG, JPEG, PNG</BulletDescription>
                        <BulletDescription>• Max file size 2MB</BulletDescription>
                    </Grid>
                    <Grid item xs={7}>
                        <ImageUpload
                            variant="mobileHero"
                            imageUrl={mobileHeroState?.url}
                            imageId={mobileHeroState?.id || 0}
                            previewId="sellerMobileHeroImagePreview"
                            maxSizeMB={2}
                            error={mobileHeroError}
                            validate={setMobileHeroError}
                            onEdit={(file) => setMobileHeroState({ url: file, id: 0 })}
                            onDelete={() => { setMobileHeroState({ url: null, id: 0 }); setMobileHeroError(null) }}
                        />
                    </Grid>
                </Grid>

                <Grid container columnSpacing={5} style={{ marginTop: '24px' }}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">Tagline</DialogSubsectionHeadline>
                        <p className={classes.inputDescription}>The tagline should explain your brand in a short sentence.</p>
                    </Grid>
                    <Grid item xs={7}>
                        <MUIFormTextAreaInput
                            name="tagline"
                            control={control}
                            rules={{ maxLength: { value: 70, message: 'Tagline cannot exceed 70 characters' } }}
                            label="Tagline"
                            shrinkLabel
                            maxLength={70}
                        />
                    </Grid>
                </Grid>

                <Grid container columnSpacing={5} style={{ marginTop: '24px' }}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">SEO</DialogSubsectionHeadline>
                        <p className={classes.inputDescription}>Manage the page meta-data for search engines.</p>
                    </Grid>
                    <Grid item xs={7}>
                        <MUIFormTextInput
                            name="seoSlugLiteral"
                            control={control}
                            label="URL Slug"
                            shrinkLabel
                        />
                    </Grid>
                </Grid>

                <Grid container columnSpacing={5} style={{ marginTop: '24px' }}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">Marketing Message</DialogSubsectionHeadline>
                        <p className={classes.inputDescription}>Add a custom message to the page.</p>
                    </Grid>
                    <Grid item xs={7}>
                        <MUISwitchInput
                            label="Show message"
                            on={displaySellerMessage}
                            name="displaySellerMessage"
                            onChange={(checked) => setDisplaySellerMessage(checked)}
                        />
                        {displaySellerMessage && (
                            <div style={{ marginTop: '20px' }}>
                                <MUIFormTextAreaInput
                                    name="sellerMessage"
                                    control={control}
                                    rules={{ maxLength: { value: 150, message: 'Marketing message cannot exceed 150 characters' } }}
                                    label="Marketing Message"
                                    shrinkLabel
                                    maxLength={150}
                                    inputPadding="25px 12px 25px"
                                />
                            </div>
                        )}
                    </Grid>
                </Grid>

                <Grid container columnSpacing={5} style={{ marginTop: '24px' }}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">About</DialogSubsectionHeadline>
                        <p className={classes.inputDescription}>This message should introduce your brand and will be displayed on your seller page.</p>
                    </Grid>
                    <Grid item xs={7}>
                        <MUIFormTextAreaInput
                            name="description"
                            control={control}
                            label="About"
                            shrinkLabel
                        />
                    </Grid>
                </Grid>

                <Grid container columnSpacing={5} style={{ marginTop: '24px' }}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">How It Works</DialogSubsectionHeadline>
                        <p className={classes.inputDescription}>This section explains how the discount is redeemed.</p>
                    </Grid>
                    <Grid item xs={7}>
                        <MUIFormTextAreaInput
                            name="howItWorks"
                            control={control}
                            label="How it works"
                            shrinkLabel
                        />
                    </Grid>
                </Grid>

            </div>
            {canUpdate && <Footer label="Save" processing={processing} onSubmit={handleSubmit(onSubmit)} />}
        </div>
    )
}

export default withStyles(styles)(SellerCustomizePageSection)
